import { Header } from '@components/Header';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { useAPI } from '@providers/useAPI';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { layoutsKeys } from 'app/queries/layouts/layoutsKeys';
import { createFetchAllOrganizationsQueryFn } from 'app/queries/organizations/createFetchAllOrganizationsQueryFn';
import { createFetchOrganizationsQueryFn } from 'app/queries/organizations/createFetchOrganizationsQueryFn';
import { organizationsKeys, TOrganizationsListParams } from 'app/queries/organizations/organizationsKeys';
import { useMemo } from 'react';
import { createFetchOrganizationLayoutsQueryFn } from '../../api/fetchOrganizationLayouts';
import { classifyLayoutsProbe } from '../../helpers/classifyLayoutsProbe';
import { filterOrganizationsWithLayouts } from '../../helpers/filterOrganizationsWithLayouts';
import { DEFAULT_LAYOUTS_LIST_PARAMS, LAYOUTS_STALE_TIME_MS } from '../../helpers/layoutsListDefaults';
import { OrganizationLayoutsCard } from './components/OrganizationLayoutsCard';
import { EmptyState } from './styles/EmptyState';
import { MainContent } from './styles/MainContent';

const SEARCH_DEBOUNCE_MS = 300;

export const Content = () => {
  const { call } = useAPI();
  const { page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize } = useListQueryParams({
    defaults: { pageSize: 10, orderBy: 'createdAt', orderType: 'desc' },
  });
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const listParams = useMemo<TOrganizationsListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch }),
    [debouncedSearch, orderBy, orderType, page, pageSize],
  );

  const organizationsQuery = useQuery({
    queryKey: organizationsKeys.list(listParams),
    queryFn: createFetchOrganizationsQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });
  const allOrganizationsQuery = useQuery({
    queryKey: organizationsKeys.allList(),
    queryFn: createFetchAllOrganizationsQueryFn(call),
  });

  const organizations = useMemo(() => organizationsQuery.data?.data ?? [], [organizationsQuery.data]);
  const organizationsCount = organizationsQuery.data?.count ?? 0;
  const allOrganizations = useMemo(() => allOrganizationsQuery.data ?? [], [allOrganizationsQuery.data]);

  const layoutProbes = useQueries({
    queries: organizations.map((organization) => ({
      queryKey: layoutsKeys.list(organization.id, DEFAULT_LAYOUTS_LIST_PARAMS),
      queryFn: createFetchOrganizationLayoutsQueryFn(call),
      placeholderData: keepPreviousData,
      staleTime: LAYOUTS_STALE_TIME_MS,
    })),
  });
  const isProbingLayouts = layoutProbes.some((probe) => probe.isPending);
  const organizationsWithLayouts = filterOrganizationsWithLayouts(
    organizations,
    layoutProbes.map((probe) => classifyLayoutsProbe(probe)),
  );
  const hiddenOrganizationsCount = organizations.length - organizationsWithLayouts.length;

  const organizationOptions = useMemo(
    () =>
      (allOrganizations.length ? allOrganizations : organizations).map((org) => ({
        label: org.name,
        value: org.id,
      })),
    [allOrganizations, organizations],
  );

  return (
    <>
      <Header title="Cloud Layouts" search={search} setSearch={setSearch} />
      <LinearProgressLoader active={organizationsQuery.isFetching || isSearchSettling || isProbingLayouts} />
      <MainContent>
        {organizationsWithLayouts.map((org) => (
          <OrganizationLayoutsCard key={org.id} organization={org} organizationOptions={organizationOptions} />
        ))}
        {organizations.length && !organizationsWithLayouts.length && !isProbingLayouts ? (
          <EmptyState>None of the organizations on this page have cloud layouts.</EmptyState>
        ) : null}
        {hiddenOrganizationsCount > 0 && !isProbingLayouts ? (
          <p className="text-xs text-[var(--color-text-muted)]">
            {hiddenOrganizationsCount} {hiddenOrganizationsCount === 1 ? 'organization' : 'organizations'} on this page
            without cloud layouts {hiddenOrganizationsCount === 1 ? 'is' : 'are'} hidden.
          </p>
        ) : null}
        <PagePagination
          totalItemsCount={organizationsCount}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </MainContent>
    </>
  );
};
